import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classnames from 'classnames';
import Mark from 'mark.js';
import {
  Button,
  Label,
  Input,
  UncontrolledTooltip,
} from 'reactstrap';
import {
  ControlledTreeEnvironment,
  Tree,
} from 'react-complex-tree';
import 'react-complex-tree/lib/style.css';
import { useIntl } from 'react-intl';

import { showModalTop } from '../../actions';
import {
  getCollectionsTree,
  clearCollectionsTree,
} from '../../actions/Collections';
import { showModalTopSecondMini } from '../../actions/Mini';

import { isArchive } from '../../helpers/App';
import {
  userCan,
  userHasCap,
} from '../../helpers/Auth';

import LoadingSmall from '../LoadingSmall';

const CollectionsTree = ({
  dispatch,
  history,
  collectionsTree,
  collectionsTreeLoading,
  highlightedCollection,
  collectionGroupId,
  selectedCollections,
  multiSelect,
  enableMyCollections,
  hideMyCollections,
  onlyMyCollections,
  enableAddNew,
  addNewDisabled,
  enableAllCollections,
  enableSearch,
  onlyOpenCollections,
  links,
  source,
  hiddenCollections,
  disabledCollections,
  onChange,
  onTransition,
  miniModal,
  defaultSort,
  smartCollectionsRestrict,
  showSmartSection,
  bypassMyCollectionsRestriction,
}) => {
  const intl = useIntl();

  const [checked, setChecked] = useState(selectedCollections);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [archive, setArchive] = useState(isArchive());
  const searchInput = useRef();

  let expanding = false;
  let collapsing = false;
  let adding = false;
  let focused = false;

  const onlyMyCollectionsEffective = onlyMyCollections || (!userHasCap('create_collection_all') && !bypassMyCollectionsRestriction);

  const treeId = `collection-tree-${source}`;

  const scrollToSelectedElement = () => {
    setTimeout(() => {
      let tree = document.getElementsByClassName('add-to-collection-form')[0];
      if (!tree) {
        tree = document.getElementsByClassName('new-collection-form')[0];
      }
      if (tree) {
        const el = tree.getElementsByClassName('rct-tree-item-li-selected')[0];
        const container = tree.getElementsByClassName('rct-tree-root')[0];
        if (el && container) {
          container.scrollTop = el.offsetTop - 30;
        }
      }
    }, 100);
  };

  useEffect(() => {
    setInitialLoading(true);
    dispatch(getCollectionsTree({
      selectedCollections,
      source,
      ...(smartCollectionsRestrict !== null && { smart: smartCollectionsRestrict }),
    })).then((response) => {
      setExpanded(Object.keys(response.sc || {})
        .map(c => (response.sc[c].expanded ? response.sc[c].uuid : null))
        .filter(c => c !== null));
      if ((!response.sc || response.sc.length === 0) && selectedCollections.length > 0) {
        // Reset checked if selected collections on backend are undefined
        if (onlyMyCollectionsEffective) {
          setChecked(['-1']);
        } else if (enableAllCollections) {
          setChecked(['all']);
        } else {
          setChecked([]);
        }
      }
      if (onlyMyCollectionsEffective) {
        setChecked(['-1']);
      }
      scrollToSelectedElement();
    });

    return () => {
      dispatch(clearCollectionsTree(source));
    };
  }, [dispatch, defaultSort]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (expanded !== null) {
      setInitialLoading(false);
    }
  }, [expanded]);

  useEffect(() => {
    onChange(checked);
  }, [checked]); // eslint-disable-line react-hooks/exhaustive-deps

  const markSearchTerm = (term) => {
    const context = document.querySelectorAll('.modal .rct-tree-item-button span');
    const instance = new Mark(context);
    instance.mark(term, { element: 'span', className: 'mark' });
  };

  useEffect(() => {
    markSearchTerm(search);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  useEffect(() => {
    if (links) {
      window.jQuery('.sidebar-nav .collections-tree .rct-tree-item-button').removeClass('rct-tree-item-button-current');
      if (highlightedCollection) {
        window.jQuery(`.sidebar-nav .collections-tree .rct-tree-item-button[data-rct-item-id="${highlightedCollection.uuid}"]`).addClass('rct-tree-item-button-current');
      } else if (collectionGroupId === -1) {
        window.jQuery('.sidebar-nav .collections-tree .rct-tree-item-button[data-rct-item-id="-1"]').addClass('rct-tree-item-button-current');
      }
    }
  }, [links, checked, expanded, highlightedCollection, collectionGroupId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedLoadTree = useCallback(window._.debounce((s) => {
    setSearching(true);
    if (s) {
      dispatch(getCollectionsTree({
        selectedCollections: checked,
        search: s,
        source,
        ...(smartCollectionsRestrict !== null && { smart: smartCollectionsRestrict }),
      }))
        .then((response) => {
          setSearching(false);
          if (response.sc) {
            setExpanded(Object.keys(response.sc)
              .map(c => (response.sc[c].expanded ? response.sc[c].uuid : null))
              .filter(c => c !== null));
          }
          markSearchTerm(s);
        });
    } else {
      dispatch(getCollectionsTree({
        selectedCollections: checked,
        source,
        ...(smartCollectionsRestrict !== null && { smart: smartCollectionsRestrict }),
      })).then((response) => {
        setSearching(false);
        setExpanded(Object.keys(response.sc)
          .map(c => (response.sc[c].expanded ? response.sc[c].uuid : null))
          .filter(c => c !== null));
      });
    }
  }, 500), []);

  const loadSubtree = (parentUuid) => {
    dispatch(getCollectionsTree({
      parentUuid,
      source,
      ...(search && { search }),
      ...(smartCollectionsRestrict !== null && { smart: smartCollectionsRestrict }),
    }));
  };

  const clearSearch = (e) => {
    e.preventDefault();
    setSearch('');
    searchInput.current.value = '';
    debouncedLoadTree('');
  };

  useEffect(() => {
    if (isArchive() !== archive && source === 'sidebar') {
      setArchive(isArchive());
      dispatch(getCollectionsTree({
        source,
        ...(smartCollectionsRestrict !== null && { smart: smartCollectionsRestrict }),
      }));
    }
  }, [history.location.pathname, archive, dispatch, source, smartCollectionsRestrict]);

  const handleOpenCollectionEditModal = (e) => {
    e.preventDefault();

    const showFunction = miniModal === 'MiniFileUploadModal' ? showModalTopSecondMini : showModalTop;

    dispatch(showFunction('CollectionEditModal', {
      className: 'modal-edit-collection',
      inOverlay: true,
      top: true,
      showSmartSection,
      afterCreateCollection: (collection) => {
        if (collection) {
          let ch = checked;
          if (multiSelect) {
            ch.push(collection.uuid);
          } else {
            ch = [collection.uuid];
          }
          setChecked(ch);

          const ex = expanded;
          collection.ancestors.reverse().forEach((a) => {
            ex.push(a.uuid);
          });
          setExpanded(ex);

          dispatch(getCollectionsTree({
            selectedCollections: ch,
            source,
            ...(smartCollectionsRestrict !== null && { smart: smartCollectionsRestrict }),
          })).then((response) => {
            setExpanded(Object.keys(response.sc)
              .map(c => (response.sc[c].expanded ? response.sc[c].uuid : null))
              .filter(c => c !== null));
            onChange(ch);
            scrollToSelectedElement();
          });
        }
      },
    }));
  };

  const handleRemoveSelectedCollection = (collectionUuid) => {
    setChecked(checked.filter(c => (c !== collectionUuid)));
  };

  const getCollection = collectionUuid => (
    collectionsTree[source][collectionUuid] || null
  );

  const hiddenAndClosedCollections = [...hiddenCollections];
  if (onlyOpenCollections) {
    Object.keys(collectionsTree[source]).forEach((key) => {
      if (!['root', '-1'].includes(key) && !collectionsTree[source][key].open) {
        hiddenAndClosedCollections.push(key);
      }
    });
  }
  if (!enableAllCollections || search) {
    hiddenAndClosedCollections.push('all');
  }

  if (hideMyCollections || !userHasCap('my_collections')) {
    hiddenAndClosedCollections.push('-1');
  }

  const collectionsToDisplay = collectionsTree[source];
  if (onlyMyCollectionsEffective) {
    collectionsToDisplay.root.children = ['-1'];
  }

  const addNewEnabled = enableAddNew && userCan('collection.create');

  return (
    <div className={`collections-tree-wrapper${links ? ' collections-tree-links' : ''}${addNewEnabled ? ' collections-tree-add-new' : ''}`}>
      {!initialLoading && (
        <>
          {enableSearch && (
            <div className="collections-tree-search-wrapper">
              <Label htmlFor="collections-tree-search">
                <i className="fas fa-search" />
              </Label>
              <Input
                type="text"
                id="collections-tree-search"
                name="collections-tree-search"
                innerRef={searchInput}
                onChange={(e) => {
                  debouncedLoadTree(e.target.value);
                  setSearch(e.target.value);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                placeholder={intl.formatMessage({ defaultMessage: 'Search collection name' })}
              />
              {search && (
                <a href="#clear-search" onClick={clearSearch}>
                  <i className="fas fa-times" />
                </a>
              )}
              {addNewEnabled && (
                <>
                  <Button
                    id="tree-create-new-collection"
                    color="primary"
                    disabled={addNewDisabled}
                    onClick={handleOpenCollectionEditModal}
                  >
                    <i className="fas fa-plus" />
                  </Button>
                  <UncontrolledTooltip target="tree-create-new-collection" fade={false}>
                    {intl.formatMessage({ defaultMessage: 'New Collection' })}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
          )}
        </>
      )}
      <div className="collections-tree">
        {(initialLoading === true || searching) ? (
          <LoadingSmall />
        ) : (
          <>
            <ControlledTreeEnvironment
              items={window._.omit(collectionsToDisplay, hiddenAndClosedCollections)}
              getItemTitle={item => item.name}
              viewState={{
                [treeId]: {
                  selectedItems: checked,
                  expandedItems: expanded,
                },
              }}
              defaultInteractionMode="click-arrow-to-expand"
              autoFocus={false}
              canRename={false}
              canSearch={false}
              onExpandItem={(item) => {
                expanding = true;
                if (!search && (item.children.length === 0 || source === 'sidebar')) {
                  loadSubtree(item.index);
                }
                setExpanded([...expanded, item.index]);
              }}
              onCollapseItem={(item) => {
                collapsing = true;
                setExpanded(expanded.filter(e => e !== item.index));
              }}
              onFocusItem={(item) => {
                if (links) {
                  return;
                }

                if (checked.includes(item.uuid)) {
                  focused = item.uuid;
                }
              }}
              onSelectItems={(selectedItems) => {
                if (expanding || collapsing || adding) {
                  expanding = false;
                  collapsing = false;
                  adding = false;
                  return;
                }

                if (links) {
                  const item = selectedItems.length > 0 ? selectedItems[0] : null;
                  if (item.indexOf('_') === 0) {
                    return;
                  }
                  let urlSlug = `/${item}`;
                  if (item === '-1') {
                    urlSlug = '/my';
                  }
                  if (item === 'all') {
                    urlSlug = '';
                  }
                  if (isArchive()) {
                    history.push(`/archive/collections${urlSlug}`);
                  } else {
                    history.push(`/collections${urlSlug}`);
                  }
                  onTransition();
                  return;
                }

                let items = selectedItems;
                if (!enableMyCollections) {
                  items = items.filter(i => (i !== '-1'));
                }

                if (focused !== false) {
                  if (multiSelect) {
                    if (checked.includes(focused)) {
                      items = items.filter(i => (i !== focused));
                      setChecked(checked.filter(c => (c !== focused)));
                    }
                  } else {
                    items = items.filter(i => (i !== focused));
                  }
                }
                focused = false;

                if (items.filter(i => (i.indexOf('_') === 0)).length > 0) {
                  return;
                }

                if (multiSelect) {
                  if (items.length > 0) {
                    setChecked(checked.concat(items));
                  }
                } else if (items.length > 0) {
                  if (!disabledCollections.includes(items[0])) {
                    setChecked([items[0]]);
                  }
                } else if (onlyMyCollectionsEffective) {
                  setChecked(['-1']);
                } else if (enableAllCollections) {
                  setChecked(['all']);
                } else {
                  setChecked([]);
                }
              }}
              renderItemTitle={({ title, item }) => (
                <>
                  <span
                    className={classnames({
                      inactive: !item.hasChildren && !search && !(item.uuid === 'all' || item.uuid.indexOf('_') !== 0),
                      searched: item.searched,
                    })}
                    title={title}
                  >
                    {item.smart && (
                      <span className="smart-collection-icon text-glow-violet mr-1">
                        <i className="fas fa-bolt ml-0" />
                      </span>
                    )}
                    <span className="tree-collection-title">
                      {title}
                    </span>
                    {collectionsTreeLoading === item.index && (
                      <i className="far fa-spinner fa-spin" />
                    )}
                  </span>
                  {checked.includes(item.index) && (
                    <span><i className="far fa-check" /></span>
                  )}
                </>
              )}
            >
              <Tree
                treeId={treeId}
                rootItem="root"
                treeLabel={intl.formatMessage({ defaultMessage: 'Collections' })}
              />
            </ControlledTreeEnvironment>
            {collectionsToDisplay.root.children.length === 0 && (
              <div className="nothing-found">{intl.formatMessage({ defaultMessage: 'Nothing found' })}</div>
            )}
          </>
        )}
      </div>
      {(multiSelect && !initialLoading && checked.length > 0) && (
        <div className="selected-collections">
          <h6>{intl.formatMessage({ defaultMessage: 'Selected collections' })}</h6>
          <div className="tags-list">
            {checked.map((c, idx) => {
              const collection = getCollection(c);
              if (!collection) {
                return null;
              }
              return (
                <div key={collection.uuid} className="tag">
                  <span className="tag-label">
                    {collection.name}
                  </span>
                  {/* eslint-disable jsx-a11y/click-events-have-key-events */}
                  <span
                    className="tag-remove"
                    title="Remove"
                    role="button"
                    tabIndex={idx}
                    onClick={() => {
                      handleRemoveSelectedCollection(c);
                    }}
                  >
                    <i className="far fa-times" />
                  </span>
                  {/* eslint-enable jsx-a11y/click-events-have-key-events */}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  collectionsTree: state.Collections.collectionsTree,
  collectionsTreeLoading: state.Collections.collectionsTreeLoading,
  highlightedCollection: state.Collections.collection,
  collectionGroupId: state.Collections.collectionGroupId,
  miniModal: state.Mini.modal,
  defaultSort: state.Auth.profile?.settings?.default_sort,
});

CollectionsTree.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  collectionsTree: PropTypes.object.isRequired,
  collectionsTreeLoading: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  highlightedCollection: PropTypes.object,
  collectionGroupId: PropTypes.number.isRequired,
  selectedCollections: PropTypes.array,
  multiSelect: PropTypes.bool,
  enableMyCollections: PropTypes.bool,
  onlyMyCollections: PropTypes.bool,
  hideMyCollections: PropTypes.bool,
  enableAddNew: PropTypes.bool,
  addNewDisabled: PropTypes.bool,
  enableAllCollections: PropTypes.bool,
  enableSearch: PropTypes.bool,
  onlyOpenCollections: PropTypes.bool,
  links: PropTypes.bool,
  source: PropTypes.string,
  hiddenCollections: PropTypes.array,
  disabledCollections: PropTypes.array,
  onChange: PropTypes.func,
  onTransition: PropTypes.func,
  miniModal: PropTypes.string,
  defaultSort: PropTypes.string,
  smartCollectionsRestrict: PropTypes.bool,
  showSmartSection: PropTypes.bool,
  bypassMyCollectionsRestriction: PropTypes.bool,
};

CollectionsTree.defaultProps = {
  collectionsTreeLoading: null,
  selectedCollections: [],
  multiSelect: false,
  enableMyCollections: true,
  onlyMyCollections: false,
  hideMyCollections: false,
  enableAddNew: true,
  addNewDisabled: false,
  enableAllCollections: false,
  enableSearch: true,
  onlyOpenCollections: false,
  links: false,
  highlightedCollection: null,
  source: 'tree',
  miniModal: null,
  defaultSort: null,
  hiddenCollections: [],
  disabledCollections: [],
  smartCollectionsRestrict: null,
  showSmartSection: true,
  bypassMyCollectionsRestriction: false,
  onChange: () => {},
  onTransition: () => {},
};

export default withRouter(connect(mapStateToProps)(CollectionsTree));
